import type { ComponentType } from 'react';

import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import StairsRoundedIcon from '@mui/icons-material/StairsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import type { SvgIconProps } from '@mui/material';

import type { PromotionActionType } from './types';

export type RewardTypeGroup = 'discount' | 'shipping' | 'gift' | 'loyalty';

export interface RewardTypeDef {
  type: PromotionActionType;
  group: RewardTypeGroup;
  icon: ComponentType<SvgIconProps>;
  /** Round-1 has a working param form; Phase 3 entries render disabled ("Coming soon"). */
  available: boolean;
}

/**
 * Data-driven reward-type gallery (plan-011 §8.3-D). Phase 3 entries are listed (so the menu
 * shows the whole catalog the runtime already executes) but rendered disabled — their param forms
 * are not built in this pass; adding one later is a config entry, not a redesign.
 */
export const REWARD_TYPES: RewardTypeDef[] = [
  { type: 'PERCENT_OFF', group: 'discount', icon: PercentRoundedIcon, available: true },
  { type: 'AMOUNT_OFF', group: 'discount', icon: AttachMoneyRoundedIcon, available: true },
  { type: 'FIXED_PRICE', group: 'discount', icon: LocalOfferRoundedIcon, available: true },
  { type: 'BUY_X_GET_Y', group: 'discount', icon: RedeemRoundedIcon, available: false },
  { type: 'TIERED_PERCENT', group: 'discount', icon: StairsRoundedIcon, available: false },
  { type: 'BUNDLE_PRICE', group: 'gift', icon: Inventory2RoundedIcon, available: false },
  { type: 'FREE_SHIPPING', group: 'shipping', icon: LocalShippingRoundedIcon, available: true },
  {
    type: 'SHIPPING_PERCENT_OFF',
    group: 'shipping',
    icon: LocalShippingRoundedIcon,
    available: true,
  },
  { type: 'GIFT_PRODUCT', group: 'gift', icon: CardGiftcardRoundedIcon, available: false },
  {
    type: 'LOYALTY_POINTS_MULTIPLIER',
    group: 'loyalty',
    icon: LoyaltyRoundedIcon,
    available: false,
  },
  { type: 'CASHBACK', group: 'loyalty', icon: SavingsRoundedIcon, available: false },
];

export const REWARD_TYPE_GROUPS: RewardTypeGroup[] = ['discount', 'shipping', 'gift', 'loyalty'];

/** `PercentOff`/`AmountOff` "apply to" target — shared by the two discount action forms. */
export type ActionTarget = 'ORDER' | 'MATCHED_LINES' | 'CHEAPEST_LINE' | 'MOST_EXPENSIVE_LINE';

export const ACTION_TARGETS: ActionTarget[] = [
  'ORDER',
  'MATCHED_LINES',
  'CHEAPEST_LINE',
  'MOST_EXPENSIVE_LINE',
];
